import { LegalLayout } from "@/components/site/LegalLayout";

// Terms of Service — content scraped from dannyclans.com (June 15, 2026
// baseline) and updated per the July 2026 legal-review task list:
//   • Task 8  — Platform definition now covers both domains.
//   • Task 9  — Wallet balance return clause on termination (Section 11).
//   • Task 10 — Liability-cap carve-out (Section 15).
//   • Task 11 — Boilerplate: Severability / Entire Agreement / Force
//                Majeure / Assignment / Notices / Sanctions.
//   • Task 12 — KYC/AML cooperation obligation.
//   • Task 13 — Modifications require 15-day email notice for material changes.
//   • Task 14 — Platform-ban risk shift (Meta/Google/TikTok bans =
//                not our liability, consumed spend non-refundable).
// Contact email standardized to help@dannyclans.com per July 2026 audit.
export default function TermsPage() {
  return (
    <LegalLayout
      title="Terms of Service"
      lastUpdated="August 3, 2026"
      path="/terms-of-service"
      metaTitle="Terms of Service"
      metaDescription="Terms of Service governing access to the DC Dashboard platform operated by Danny Clans Media Limited. Wallet, refund, liability, KYC & jurisdiction clauses."
    >
      <p>
        These Terms of Service ("Terms") govern access to and use of the
        DC Dashboard platform accessible at{" "}
        <a href="https://dannyclans.solutions">dannyclans.solutions</a> and{" "}
        <a href="https://dannyclans.com">dannyclans.com</a> ("Platform"),
        operated by Danny Clans Media Limited ("Company", "we", "our", or "us").
      </p>
      <p>
        By creating an account, accessing the Platform, funding a wallet, requesting
        advertising account access, or using any services provided through the Platform,
        you agree to be bound by these Terms.
      </p>

      <h2>1. Eligibility</h2>
      <p>You represent and warrant that:</p>
      <ul>
        <li>You are at least 18 years old.</li>
        <li>You have authority to bind any company or organization on whose behalf you use the Platform.</li>
        <li>All information provided during registration is accurate and complete.</li>
        <li>Your use of the Platform complies with all applicable laws and regulations.</li>
      </ul>

      <h2>2. Services</h2>
      <p>
        The Platform provides access to advertising infrastructure, advertising accounts,
        wallet funding functionality, campaign management tools, and related services.
      </p>
      <p>
        The Company may add, modify, suspend, or discontinue any feature, service, or
        advertising platform integration at any time without liability.
      </p>

      <h2>3. Ownership of Advertising Accounts &amp; Platform Risk</h2>
      <p>
        All advertising accounts made available through the Platform remain the sole
        property of the Company or its licensors. Users receive a limited, revocable,
        non-exclusive, non-transferable right to use such accounts solely for approved
        advertising activities. Users acquire no ownership rights in any advertising account.
      </p>
      <p>
        The Company may revoke access at any time for policy violations, fraud concerns,
        non-payment, excessive risk, or platform abuse.
      </p>
      <p>
        <strong>Third-party platform actions.</strong> The Company is not liable for
        actions taken by third-party advertising platforms, including but not limited to
        Meta, Google, TikTok, Bing, Snap, LinkedIn, YouTube, Outbrain, Taboola, MediaGo,
        and any other advertising platform. This includes account bans, restrictions,
        suspensions, disapprovals, throttling, spend limits, or content removals imposed
        by such third parties. Any advertising spend already consumed prior to a
        platform-level action is non-refundable regardless of the outcome of any appeal.
      </p>

      <h2>4. Prohibited Content</h2>
      <p>Users may not promote or distribute:</p>
      <ul>
        <li>Illegal products or services</li>
        <li>Fraudulent offers</li>
        <li>Counterfeit goods</li>
        <li>Copyright-infringing content</li>
        <li>Misleading financial products</li>
        <li>Cryptocurrency scams</li>
        <li>Malware, spyware, or malicious software</li>
        <li>Adult content prohibited by applicable platform policies</li>
        <li>Gambling content where prohibited</li>
        <li>Hate speech or discriminatory content</li>
        <li>Violent or harmful content</li>
        <li>Any content violating advertising platform policies</li>
      </ul>
      <p>The Company may remove content or suspend access without notice.</p>

      <h2>5. Wallet System</h2>
      <ul>
        <li>Users may fund a wallet through approved payment methods.</li>
        <li>Wallet balances represent funds available for advertising-related services only.</li>
        <li>Wallet balances are not bank deposits.</li>
        <li>Wallet balances do not earn interest.</li>
        <li>The Company may impose minimum funding thresholds, withdrawal limits, verification requirements, or reserve periods.</li>
      </ul>

      <h2>6. Refunds</h2>
      <ul>
        <li>Subscription fees are non-refundable unless otherwise stated.</li>
        <li>Advertising spend already consumed by advertising platforms is non-refundable.</li>
        <li>Wallet withdrawals may be subject to verification procedures, fraud reviews, compliance reviews, processing delays, and payment provider restrictions.</li>
        <li>The Company reserves the right to refuse refunds where fraud, abuse, chargebacks, or policy violations are suspected.</li>
      </ul>

      <h2>7. Payments</h2>
      <p>Users are responsible for all:</p>
      <ul>
        <li>Credit card fees</li>
        <li>Processing fees</li>
        <li>Wire transfer fees</li>
        <li>Bank charges</li>
        <li>Foreign exchange fees</li>
        <li>Intermediary banking fees</li>
        <li>Taxes and governmental charges</li>
      </ul>
      <p>The Company may suspend services for overdue balances or disputed transactions.</p>

      <h2>8. Chargebacks and Payment Disputes</h2>
      <p>Users agree not to initiate chargebacks or payment disputes for services already delivered. If a chargeback occurs:</p>
      <ul>
        <li>The Company may immediately suspend all accounts.</li>
        <li>Wallet balances may be frozen.</li>
        <li>Advertising accounts may be disabled.</li>
        <li>Collection efforts may be pursued.</li>
      </ul>
      <p>Users remain liable for all legitimate charges, fees, and collection costs.</p>

      <h2>9. Account Security</h2>
      <p>Users are responsible for maintaining the confidentiality of account credentials.</p>
      <p>The Company is not liable for losses resulting from:</p>
      <ul>
        <li>Stolen passwords</li>
        <li>Unauthorized access</li>
        <li>User negligence</li>
        <li>Credential sharing</li>
      </ul>
      <p>Users must immediately notify the Company of suspected security incidents.</p>

      <h2>10. Platform Availability</h2>
      <p>The Platform is provided on an "AS IS" and "AS AVAILABLE" basis.</p>
      <p>The Company does not guarantee:</p>
      <ul>
        <li>Continuous uptime</li>
        <li>Error-free operation</li>
        <li>Availability of specific advertising accounts</li>
        <li>Specific campaign performance</li>
        <li>Return on advertising spend</li>
      </ul>
      <p>Maintenance, upgrades, third-party outages, and platform restrictions may affect service availability.</p>

      <h2>11. Suspension, Termination &amp; Wallet Balance Return</h2>
      <p>The Company may suspend or terminate any account immediately if:</p>
      <ul>
        <li>Fraud is suspected</li>
        <li>Payment obligations are not met</li>
        <li>Advertising policies are violated</li>
        <li>Laws are violated</li>
        <li>Excessive risk is identified</li>
        <li>False information is provided</li>
      </ul>
      <p>Termination does not relieve users of outstanding payment obligations.</p>
      <p>
        <strong>Wallet balance return on termination.</strong> Following termination,
        the Company shall return the User's undisputed Wallet balance within thirty
        to sixty (30–60) business days, net of: (a) verified outstanding liabilities;
        (b) pending advertising spend not yet reconciled with advertising platforms;
        and (c) reasonable processing, banking, and compliance costs. The Company may
        extend this period where additional identity verification, chargeback
        investigation, sanctions screening, or regulatory review is required. Return
        will be made to the same payment method used for funding where feasible, or to
        an alternative account nominated in writing by the User and verified by the
        Company.
      </p>

      <h2>12. Compliance</h2>
      <p>Users are solely responsible for compliance with:</p>
      <ul>
        <li>Advertising laws</li>
        <li>Consumer protection laws</li>
        <li>Privacy laws</li>
        <li>Data protection regulations</li>
        <li>Industry-specific regulations</li>
      </ul>
      <p>The Company does not provide legal advice.</p>

      <h2>13. KYC &amp; AML Cooperation</h2>
      <p>
        Users agree to cooperate fully with the Company's Know-Your-Customer ("KYC")
        and Anti-Money-Laundering ("AML") verification procedures as set out in the{" "}
        <a href="/kyc-aml-policy">KYC &amp; AML Verification Policy</a>. Users shall provide
        requested documentation and information promptly upon request. Failure to complete
        verification, or the provision of false or misleading information, is grounds for
        suspension or termination of services and may result in the withheld return of
        wallet balances until compliance is confirmed.
      </p>

      <h2>14. Intellectual Property</h2>
      <p>
        The Platform, software, dashboards, workflows, trademarks, branding,
        documentation, and related technology remain the exclusive property of the
        Company. Users may not:
      </p>
      <ul>
        <li>Copy</li>
        <li>Reverse engineer</li>
        <li>Resell</li>
        <li>Modify</li>
        <li>Redistribute</li>
        <li>Create derivative works</li>
      </ul>
      <p>without written authorization.</p>

      <h2>15. Indemnification</h2>
      <p>
        Users agree to defend, indemnify, and hold harmless the Company, its officers,
        directors, employees, affiliates, and partners from claims arising from:
      </p>
      <ul>
        <li>User content</li>
        <li>Advertising campaigns</li>
        <li>Policy violations</li>
        <li>Legal violations</li>
        <li>Payment disputes</li>
        <li>Breach of these Terms</li>
      </ul>

      <h2>16. Limitation of Liability</h2>
      <p>
        To the fullest extent permitted by law, and subject to the carve-out below,
        the Company's total liability arising out of or relating to the Platform shall
        not exceed the total platform and service fees paid by the User during the
        three (3) months preceding the event giving rise to the claim.
      </p>
      <p>The Company shall not be liable for:</p>
      <ul>
        <li>Lost profits</li>
        <li>Lost data</li>
        <li>Lost business opportunities</li>
        <li>Indirect damages</li>
        <li>Consequential damages</li>
        <li>Punitive damages</li>
        <li>Advertising performance losses</li>
      </ul>
      <p>
        <strong>Carve-out.</strong> The limitation of liability in this Section 16
        applies only to platform and service fees. The Company's obligation to return
        the User's undisputed Wallet balance under Section 11 is expressly excluded
        from this cap and shall not be limited by it.
      </p>

      <h2>17. Confidentiality</h2>
      <p>
        Users shall maintain the confidentiality of all non-public information received
        through the Platform. This obligation survives termination.
      </p>

      <h2>18. Sanctions &amp; Restricted Persons</h2>
      <p>Users represent and warrant that they, and each of their beneficial owners, are not:</p>
      <ul>
        <li>Listed on any sanctions list maintained by the Office of Foreign Assets Control (OFAC), the United Nations Security Council, the European Union, His Majesty's Treasury (UK), Hong Kong authorities, or any equivalent authority;</li>
        <li>Ordinarily resident, domiciled, or organized in any comprehensively sanctioned jurisdiction; or</li>
        <li>Owned or controlled, directly or indirectly, by any person described above.</li>
      </ul>
      <p>
        The Company may screen Users against sanctions and watchlists at any time and
        may refuse service, freeze funds, or terminate accounts where sanctions or
        material compliance concerns are identified.
      </p>

      <h2>19. Force Majeure</h2>
      <p>
        The Company shall not be liable for any delay or failure to perform arising
        from causes beyond its reasonable control, including but not limited to: acts
        of God, war, terrorism, civil unrest, government action, sanctions, pandemic,
        strikes, third-party platform outages, internet-service failures, cyber
        incidents, banking-system disruptions, or changes in applicable law.
      </p>

      <h2>20. Assignment</h2>
      <p>
        The Company may assign, transfer, or delegate these Terms and any rights or
        obligations hereunder, in whole or in part, to any affiliate or successor
        without prior notice. Users may not assign or transfer these Terms or any
        rights hereunder without the Company's prior written consent, and any
        purported assignment in breach of this Section is void.
      </p>

      <h2>21. Notices</h2>
      <p>
        All notices to the Company shall be sent by email to{" "}
        <a href="mailto:help@dannyclans.com">help@dannyclans.com</a> and, where legal
        service is required, in writing to Danny Clans Media Limited, S022, 2/F, The
        Capital, 61–65 Chatham Road South, Tsim Sha Tsui, Hong Kong. Notices to Users
        may be delivered to the email address associated with the User's account and
        are deemed received on the date sent.
      </p>

      <h2>22. Severability</h2>
      <p>
        If any provision of these Terms is held to be invalid, illegal, or
        unenforceable, the remaining provisions shall continue in full force and
        effect, and the invalid provision shall be replaced by a valid provision that
        most closely reflects the parties' original intent.
      </p>

      <h2>23. Entire Agreement</h2>
      <p>
        These Terms, together with the{" "}
        <a href="/privacy-policy">Privacy Policy</a>,{" "}
        <a href="/refund-policy">Refund &amp; Withdrawal Policy</a>, and{" "}
        <a href="/kyc-aml-policy">KYC &amp; AML Policy</a>, constitute the entire
        agreement between the User and the Company with respect to the Platform and
        supersede all prior or contemporaneous agreements, communications, and
        proposals, whether oral or written.
      </p>

      <h2>24. Governing Law</h2>
      <p>These Terms are governed by the laws of Hong Kong.</p>

      <h2>25. Dispute Resolution</h2>
      <p>Any dispute arising from these Terms shall be subject to the exclusive jurisdiction of the courts of Hong Kong.</p>

      <h2>26. Modifications</h2>
      <p>
        The Company may update these Terms at any time. For material changes, the
        Company will provide notice via the email address associated with the User's
        account at least fifteen (15) days prior to the effective date of the revised
        Terms. Continued use of the Platform after the effective date constitutes
        acceptance of the revised Terms.
      </p>

      <h2>27. Contact Information</h2>
      <p>
        Danny Clans Media Limited<br />
        Email: <a href="mailto:help@dannyclans.com">help@dannyclans.com</a><br />
        Address: S022, 2/F, The Capital, 61–65 Chatham Road South, Tsim Sha Tsui, Hong Kong
      </p>
    </LegalLayout>
  );
}
